import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useCodingRoutes } from "../../hooks/useCodingRoutes";
import AddStudentModal from "../../components/CodingTracker/AddStudentModal";
import BulkAddStudentsModal from "../../components/CodingTracker/BulkAddStudentsModal";
import useBatchStore from "../../store/batchStore";
import useStudentStore from "../../store/studentStore";
import {
  Box, Button, Typography, Chip, CircularProgress, Alert,
  Link, Stack,
} from "@mui/material";
import GlassCard from "../../components/Cards/GlassCard";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import CommonDataGrid from "../../components/CodingTracker/CommonDataGrid";
import {
  gradientButtonSx, primaryChipSx,
  outlinedPrimaryButtonSx,
  PRIMARY, DANGER_BG,
} from "../../utils/colors";
import {
  DEFAULT_AT_RISK_INACTIVE_DAYS,
  atRiskStudentRowSx,
  isAtRiskStudent,
} from "../../utils/atRiskStudent";
import { getDataGridGlobalSerialNumber } from "../../utils/dataGridSerialNumber";
import { ctGetBatchInsights } from "../../services/api";

const BatchStudentDetailsPage = () => {
  const { batchId } = useParams();
  const cr = useCodingRoutes();

  const { currentBatch, fetchBatchById } = useBatchStore();
  const { students, fetchStudents, addStudent, updateStudent, removeStudent, deleting, loading, error } = useStudentStore();

  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  /** undefined = insights loading; null = fetch failed (fallback to lastSubmissionAt heuristic); Set = canonical ids from API */
  const [atRiskIdSet, setAtRiskIdSet] = useState(undefined);

  useEffect(() => {
    fetchBatchById(batchId);
    fetchStudents(batchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const refreshAtRiskIds = useCallback(async () => {
    if (!batchId) return;
    try {
      const { data } = await ctGetBatchInsights(batchId);
      const ids = data?.atRiskStudentIds;
      if (Array.isArray(ids)) {
        setAtRiskIdSet(new Set(ids.map((x) => String(x))));
        return;
      }
      const fromList = (data?.atRiskList ?? []).map((r) => r.studentId);
      setAtRiskIdSet(new Set(fromList.map((x) => String(x))));
    } catch {
      setAtRiskIdSet(null);
    }
  }, [batchId]);

  useEffect(() => {
    refreshAtRiskIds();
  }, [refreshAtRiskIds]);

  const handleOpenAdd = () => {
    setEditingStudent(null);
    setShowModal(true);
  };

  const handleOpenEdit = (student) => {
    setEditingStudent(student);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStudent(null);
  };

  const handleSubmitStudent = async (form) => {
    try {
      if (editingStudent) {
        await updateStudent(editingStudent._id, form);
      } else {
        await addStudent(batchId, form);
      }
      handleCloseModal();
      await fetchStudents(batchId, true);
      await refreshAtRiskIds();
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Remove this student from the batch?")) return;
    try {
      await removeStudent(studentId);
      await fetchStudents(batchId, true);
      await refreshAtRiskIds();
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
    }
  };

  const getAtRiskRowClassName = useCallback((params) => {
    const id = String(params.row._id);
    if (atRiskIdSet === undefined) return "";
    if (atRiskIdSet === null) {
      return isAtRiskStudent(params.row, DEFAULT_AT_RISK_INACTIVE_DAYS) ? "row-at-risk" : "";
    }
    return atRiskIdSet.has(id) ? "row-at-risk" : "";
  }, [atRiskIdSet]);

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 60,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => getDataGridGlobalSerialNumber(params),
    },
    {
      field: "rollNumber",
      headerName: "Roll Number",
      flex: 1,
      minWidth: 80,
      headerAlign: "center",
      renderCell: (params) => (
        <Link
          href={`https://leetcode.com/u/${params.row.leetcodeUsername}/`}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          color="primary"
          fontWeight={500}
        >
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            {params.value || "—"}
          </Typography>
        </Link>
      ),
    },
    {
      field: "fullName",
      headerName: "Student Name",
      flex: 1.5,
      minWidth: 140,
      headerAlign: "center",
      renderCell: (params) => (
        <Link
          href={`https://leetcode.com/u/${params.row.leetcodeUsername}/`}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          color="primary"
          fontWeight={500}
        >
          <Typography fontWeight={500} sx={{ pt: 2 }}>
            {params.value || "—"}
          </Typography>
        </Link>
      ),
    },

    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 160,
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => handleOpenEdit(params.row)}
            sx={{ minWidth: 40, color: PRIMARY }}
          >
            <EditRoundedIcon fontSize="small" />
          </Button>
          <Button
            size="small"
            color="error"
            disabled={deleting === params.row._id}
            onClick={() => handleDeleteStudent(params.row._id)}
            sx={{
              minWidth: 40,
              "&:hover": { color: "error.main", bgcolor: DANGER_BG },
            }}
          >
            {deleting === params.row._id ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteRoundedIcon fontSize="small" />
            )}
          </Button>
        </Stack>
      ),
    },
  ];

  if (loading)
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  if (error)
    return <Box p={4}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto", minHeight: "100vh" }}>
      <AppBreadcrumbs items={[
        { label: 'Coding Tracker', path: cr.trackerHome },
        { label: currentBatch?.name || 'Batch', path: cr.batch(batchId) },
        { label: 'Students' },
      ]} />

      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsRoundedIcon sx={{ color: PRIMARY }} />
            <Typography variant="h6" fontWeight={700}>Students</Typography>
            <Chip label={students.length} size="small" sx={primaryChipSx} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setShowBulkModal(true)} sx={outlinedPrimaryButtonSx}>
              Bulk Add
            </Button>
            <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={handleOpenAdd} sx={{ ...gradientButtonSx, px: 3 }}>
              Add Student
            </Button>
          </Stack>
        </Box>

        {students.length === 0 ? (
          <GlassCard hoverable={false} sx={{ textAlign: "center", py: 10, border: "2px dashed", borderColor: "divider", borderRadius: 4 }}>
            <Typography variant="h6" color="text.secondary" mb={1}>No students yet</Typography>
            <Typography variant="body2" color="text.disabled" mb={3}>
              Add students to start tracking their LeetCode progress.
            </Typography>
            <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={handleOpenAdd} sx={{ ...gradientButtonSx, px: 3 }}>
              Add first student
            </Button>
          </GlassCard>
        ) : (
          <GlassCard hoverable={false} sx={{ borderRadius: 4, overflow: "hidden" }}>
            <CommonDataGrid
              firstColumnField="index"
              rows={students.filter(Boolean).map((s) => ({ ...s, id: s._id }))}
              columns={columns}
              getRowClassName={getAtRiskRowClassName}
              sx={atRiskStudentRowSx}
            />
          </GlassCard>
        )}
      </Box>

      {showModal && (
        <AddStudentModal
          onClose={handleCloseModal}
          onSubmit={handleSubmitStudent}
          initialData={editingStudent}
        />
      )}
      {showBulkModal && (
        <BulkAddStudentsModal open={showBulkModal} onClose={() => setShowBulkModal(false)}
          batchId={batchId} onSuccess={async () => { await fetchStudents(batchId, true); await refreshAtRiskIds(); }} />
      )}
    </Box>
  );
};

export default BatchStudentDetailsPage;
